import axios, { type AxiosRequestConfig } from 'axios';

const LOCALSTORAGE_TOKEN_KEY = 'DS';

interface CancellablePromise<T> extends Promise<T> {
  cancel: () => void;
}

const AXIOS_INSTANCE = axios.create({
  baseURL: process.env.VITE_API_BASE_URL
});

export const useCustomAxios =
  <T>(): ((config: AxiosRequestConfig) => CancellablePromise<T>) =>
  (config: AxiosRequestConfig) => {
    const authToken = localStorage.getItem(LOCALSTORAGE_TOKEN_KEY);

    const source = axios.CancelToken.source();
    const promise = AXIOS_INSTANCE({
      ...config,
      paramsSerializer: {
        encode: param =>
          /**
           * by default axios, after encoding the parameter, turns back
           * special symbols (especially '[' and ']') in the url
           * which breaks JSON stringified object as parameter for backend
           * source: https://github.com/axios/axios/blob/v1.x/lib/helpers/buildURL.js#L14 */
          encodeURIComponent(param)
      },
      headers: {
        ...config.headers,
        Authorization: `Bearer ${authToken}`
      },
      cancelToken: source.token
    }).then(({ data }) => data) as CancellablePromise<T>;

    promise.cancel = () => {
      source.cancel('Query was cancelled by React Query');
    };

    return promise;
  };

export const useCustomBinaryAxios = <T>(): ((config: AxiosRequestConfig<ArrayBuffer>) => CancellablePromise<T>) => {
  const customAxios = useCustomAxios<T>();

  return (config: AxiosRequestConfig<ArrayBuffer>) =>
    customAxios({
      ...config,
      responseType: 'arraybuffer'
    }) as CancellablePromise<T>;
};
